import React, { useState } from 'react';
import { Phone, MapPin, Facebook, CheckCircle, Home, Droplets, Shield, Trees, School, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Call the backend API
      const response = await axios.post(`${BACKEND_URL}/api/inquiry`, formData);
      
      if (response.status === 200) {
        toast.success('Thank you! We will contact you soon. Please check your email for confirmation.');
        setFormData({ name: '', phone: '', email: '' });
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or call us directly.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const amenities = [
    {
      icon: Shield,
      title: 'Gated Community',
      description: 'Safe and secure perimeter with 24/7 security surveillance',
      image: 'https://images.unsplash.com/photo-1722293862101-6511f044073b'
    },
    {
      icon: Home,
      title: 'Wide Roads & Power',
      description: 'Premium bitumen/concrete roads with state-of-the-art electrical grid',
      image: 'https://customer-assets.emergentagent.com/job_mumbai-style-plots/artifacts/w75286qr_image.png'
    },
    {
      icon: Droplets,
      title: 'Central Water Tank',
      description: 'Massive water infrastructure ensuring 24/7 pressurized supply',
      image: 'https://customer-assets.emergentagent.com/job_mumbai-style-plots/artifacts/a86ttcmn_image.png'
    },
    {
      icon: Trees,
      title: 'Landscaped Parks',
      description: 'Beautiful green spaces and community gardens for families',
      image: 'https://images.unsplash.com/photo-1718206798129-b9b9c3ec48a3'
    },
    {
      icon: School,
      title: 'School Infrastructure',
      description: 'Dedicated space for educational facilities within the community',
      image: 'https://customer-assets.emergentagent.com/job_mumbai-style-plots/artifacts/52arw33m_image.png'
    },
    {
      icon: TrendingUp,
      title: 'Prime Location',
      description: 'Strategic road access on Baran Road with excellent connectivity',
      image: 'https://customer-assets.emergentagent.com/job_mumbai-style-plots/artifacts/6zi6tgc7_image.png'
    }
  ];

  const trustFactors = [
    'RERA Certified Project',
    'Mumbai-Designed Master Plan',
    'Legal & Clear Titles',
    'Transparent Documentation'
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header-nav">
        <div className="container-custom">
          <div className="header-content">
            <div className="logo">
              <h1 className="logo-text">Seth Sanwariya Dham</h1>
              <p className="logo-subtitle">RERA Certified</p>
            </div>
            <nav className="nav-links">
              <a href="#amenities" className="nav-link">Amenities</a>
              <a href="#location" className="nav-link">Location</a>
              <a href="#contact" className="nav-link">Contact</a>
              <Button 
                onClick={() => window.open('tel:+912262054536')}
                className="cta-button-header"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container-custom hero-content-wrapper">
          <div className="hero-text">
            <div className="trust-badge">
              <CheckCircle className="w-5 h-5" />
              <span>RERA Certified Project</span>
            </div>
            <h1 className="hero-title">
              Legacy Lands.<br />
              Luxurious Living.<br />
              <span className="hero-title-accent">Unwavering Trust</span>
            </h1>
            <p className="hero-description">
              Experience Mumbai-designed urban planning in Kota, Rajasthan. Premium residential plots with world-class infrastructure and amenities.
            </p>
            <div className="hero-cta-group">
              <Button 
                size="lg"
                className="hero-cta-primary"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Inquiry Now
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="hero-cta-secondary"
                onClick={() => window.open('tel:+912262054536')}
              >
                <Phone className="w-5 h-5" />
                +91 226 205 4536
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="trust-section">
        <div className="container-custom">
          <div className="trust-grid">
            {trustFactors.map((factor, index) => (
              <div key={index} className="trust-item">
                <CheckCircle className="trust-icon" />
                <span className="trust-text">{factor}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mumbai Master Plan Section */}
      <section className="master-plan-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Mumbai-Designed Master Plan</h2>
            <p className="section-subtitle">
              Urban efficiency meets luxurious living. Our master plan is designed by Mumbai's top urban planners, bringing metropolitan sophistication to Kota.
            </p>
          </div>
          <div className="master-plan-grid">
            <div className="master-plan-image">
              <img 
                src="https://images.unsplash.com/photo-1574968641537-9c0615d1ec0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjByZXNpZGVudGlhbCUyMHBsb3RzJTIwYWVyaWFsfGVufDB8fHx8MTc3MzU2MDQwOHww&ixlib=rb-4.1.0&q=85"
                alt="Aerial view of luxury residential development"
                className="plan-image"
              />
            </div>
            <div className="master-plan-features">
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <Home className="feature-icon" />
                </div>
                <h3 className="feature-title">Strategic Layout</h3>
                <p className="feature-description">
                  Optimized plot sizing and spacing for maximum privacy and accessibility
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <Trees className="feature-icon" />
                </div>
                <h3 className="feature-title">Green Corridors</h3>
                <p className="feature-description">
                  Integrated green spaces throughout the community for healthy living
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon-wrapper">
                  <MapPin className="feature-icon" />
                </div>
                <h3 className="feature-title">Prime Connectivity</h3>
                <p className="feature-description">
                  Direct access to Baran Road ensuring excellent transportation links
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section id="amenities" className="amenities-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">World-Class Amenities</h2>
            <p className="section-subtitle">
              RERA-compliant infrastructure designed for modern living
            </p>
          </div>
          <div className="amenities-grid">
            {amenities.map((amenity, index) => {
              const Icon = amenity.icon;
              return (
                <Card key={index} className="amenity-card">
                  <div className="amenity-image-wrapper">
                    <img 
                      src={amenity.image}
                      alt={amenity.title}
                      className="amenity-image"
                    />
                    <div className="amenity-icon-overlay">
                      <Icon className="amenity-icon" />
                    </div>
                  </div>
                  <CardContent className="amenity-content">
                    <h3 className="amenity-title">{amenity.title}</h3>
                    <p className="amenity-description">{amenity.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="location-section">
        <div className="container-custom">
          <div className="location-grid">
            <div className="location-info">
              <h2 className="section-title">Prime Location on Baran Road</h2>
              <p className="location-description">
                Strategically located on Baran Road in Kota, Rajasthan, Seth Sanwariya Dham offers unparalleled connectivity to key areas of the city while maintaining a serene residential environment.
              </p>
              <div className="location-highlights">
                <div className="location-highlight-item">
                  <MapPin className="location-highlight-icon" />
                  <div>
                    <h4 className="location-highlight-title">Excellent Connectivity</h4>
                    <p className="location-highlight-text">Direct access to Baran Road</p>
                  </div>
                </div>
                <div className="location-highlight-item">
                  <CheckCircle className="location-highlight-icon" />
                  <div>
                    <h4 className="location-highlight-title">Strategic Position</h4>
                    <p className="location-highlight-text">Close to schools, hospitals & markets</p>
                  </div>
                </div>
              </div>
              <Button 
                size="lg"
                className="location-cta"
                onClick={() => window.open('https://maps.app.goo.gl/NCnAQENU7fWab6hoE', '_blank')}
              >
                <MapPin className="w-5 h-5" />
                View on Map
              </Button>
            </div>
            <div className="location-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3544.8764924927647!2d75.86847!3d25.18456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDExJzA0LjQiTiA3NcKwNTInMDYuNSJF!5e0!3m2!1sen!2sin!4v1234567890123"
                className="map-iframe"
                allowFullScreen=""
                loading="lazy"
                title="Seth Sanwariya Dham Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="contact-section">
        <div className="container-custom">
          <div className="contact-grid">
            <div className="contact-info-wrapper">
              <h2 className="section-title">Get In Touch</h2>
              <p className="contact-description">
                Ready to invest in your legacy? Contact us today to schedule a site visit and explore the opportunities at Seth Sanwariya Dham.
              </p>
              <div className="contact-methods">
                <a href="tel:+912262054536" className="contact-method-item">
                  <div className="contact-method-icon">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="contact-method-label">Call Us</div>
                    <div className="contact-method-value">+91 226 205 4536</div>
                  </div>
                </a>
                <a href="mailto:Sethsanwariyadham@gmail.com" className="contact-method-item">
                  <div className="contact-method-icon">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="contact-method-label">Email Us</div>
                    <div className="contact-method-value">Sethsanwariyadham@gmail.com</div>
                  </div>
                </a>
                <a 
                  href="https://www.facebook.com/share/1Gv21xWADL" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="contact-method-item"
                >
                  <div className="contact-method-icon">
                    <Facebook className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="contact-method-label">Follow Us</div>
                    <div className="contact-method-value">Facebook</div>
                  </div>
                </a>
              </div>
            </div>
            <Card className="contact-form-card">
              <CardContent className="contact-form-content">
                <h3 className="contact-form-title">Request a Callback</h3>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+91 00000 00000"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <Button type="submit" size="lg" className="contact-form-submit">
                    Submit Inquiry
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container-custom">
          <div className="footer-content">
            <div className="footer-brand">
              <h3 className="footer-logo">Seth Sanwariya Dham</h3>
              <p className="footer-tagline">Legacy Lands. Luxurious Living. Unwavering Trust</p>
              <p className="footer-rera">RERA Certified Project</p>
            </div>
            <div className="footer-links">
              <h4 className="footer-links-title">Quick Links</h4>
              <a href="#amenities" className="footer-link">Amenities</a>
              <a href="#location" className="footer-link">Location</a>
              <a href="#contact" className="footer-link">Contact</a>
            </div>
            <div className="footer-contact">
              <h4 className="footer-links-title">Contact</h4>
              <p className="footer-contact-item">+91 226 205 4536</p>
              <p className="footer-contact-item">Sethsanwariyadham@gmail.com</p>
              <a 
                href="https://www.facebook.com/share/1Gv21xWADL"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social"
              >
                <Facebook className="w-5 h-5" />
                Facebook
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Seth Sanwariya Dham. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
